import type { ReactElement } from 'react';
import { Box, Text } from '@mantine/core';
import type { TopicDefinition } from './TopicCatalogTypes';
import type { AdHocAggregateResult, AnomalyResult, AnomalySeverity } from '@/shared/analytics/AnalyticsTypes';
import RankedList, { type RankedListColumn, type RankedListPrimary } from '@/design/RankedList/RankedList';
import { SEVERITY_COLORS } from '@/shared/signals/SignalSeverityColors';
import { formatCount, formatPercent, formatTokens } from '@/shared/analytics/MetricFormatting';
import { sliceToPath } from './TopicSliceKey';

interface AlertsVizProps {
    definition: TopicDefinition;
    anomalies: AnomalyResult | null;
    primary: AdHocAggregateResult;
    basePath: string;
}

interface AnomalyRow {
    drillKey?: string;
    subject: string;
    severity: AnomalySeverity;
    count: number;
    turns: number;
    totalTokens: number;
    /** Re-prompt rate — this person's re-prompts as a share of their prompts (frustration only). */
    rate: number;
}

const METRIC_WIDTH = 76;
const TOKEN_WIDTH = 92;

const SEVERITY_LABEL: Record<AnomalySeverity, string> = { high: 'High', medium: 'Med', low: 'Low' };
const SEVERITY_RANK: Record<AnomalySeverity, number> = { high: 3, medium: 2, low: 1 };

/** A labeled severity pill — replaces a bare color dot so the severity reads at a glance. */
function SeverityPill({ severity }: { severity: AnomalySeverity }): ReactElement {
    const color = SEVERITY_COLORS[severity];
    return (
        <Box
            component="span"
            style={{
                display: 'inline-flex',
                alignItems: 'center',
                flexShrink: 0,
                padding: '1px 7px',
                borderRadius: 999,
                fontSize: 9,
                fontWeight: 700,
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
                whiteSpace: 'nowrap',
                color,
                background: `color-mix(in srgb, ${color} 14%, transparent)`,
                border: `1px solid color-mix(in srgb, ${color} 30%, transparent)`,
            }}
        >
            {SEVERITY_LABEL[severity]}
        </Box>
    );
}

/**
 * The L1 view for an `alerts` topic: flagged behavioral anomalies as a ranked
 * table. The detector supplies each row's subject, severity, and headline count
 * (topics spanned for drift, re-prompts for frustration); prompts and total
 * tokens are joined from `primary` — already grouped by this topic's
 * `primaryField` (`session_id` / `user_id`). Frustration rows lead with a labeled
 * severity pill and a re-prompt-rate bar (re-prompts ÷ their prompts) so intensity
 * reads relative to activity, not just raw volume. Each row drills into the
 * underlying turns via the standard evidence route.
 */
export default function AlertsViz({ definition, anomalies, primary, basePath }: AlertsVizProps): ReactElement {
    const events = anomalies?.events ?? [];

    if (events.length === 0) {
        return (
            <Box style={{ border: '1px solid var(--bm-color-border)', borderRadius: 'var(--bm-radius)', padding: '14px 16px', background: 'var(--bm-color-surface)' }}>
                <Text size="sm" c="dimmed">
                    No {definition.lensPlural} detected in this window.
                </Text>
            </Box>
        );
    }

    const isDrift = definition.anomalyKind === 'session_drift';

    const metricsByKey = new Map<string, { turns: number; totalTokens: number }>();
    for (const row of primary.rows) {
        const key = row.keys[definition.primaryField];
        if (key !== undefined) metricsByKey.set(key, { turns: row.metrics.turns, totalTokens: row.metrics.totalTokens });
    }

    const rows: AnomalyRow[] = events.map((event) => {
        const joined = event.drillKey === undefined ? undefined : metricsByKey.get(event.drillKey);
        const turns = joined?.turns ?? 0;
        const count = event.count ?? 0;
        return {
            drillKey: event.drillKey,
            subject: event.subject ?? event.detail,
            severity: event.severity,
            count,
            turns,
            totalTokens: isDrift ? (joined?.totalTokens ?? 0) : (event.tokens ?? 0),
            rate: turns > 0 ? Math.min(1, count / turns) : 0,
        };
    });

    const hrefFor = (drillKey: string | undefined): string =>
        drillKey === undefined ? basePath : sliceToPath(basePath, definition.id, { primary: drillKey });

    const primaryCol: RankedListPrimary<AnomalyRow> = isDrift
        ? {
              key: 'subject',
              header: 'Session',
              hideBar: true,
              label: (row) => (
                  <Box component="span" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, maxWidth: '100%' }}>
                      <Box component="span" style={{ width: 8, height: 8, borderRadius: '50%', background: SEVERITY_COLORS[row.severity], flexShrink: 0 }} />
                      <Box component="span" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.subject}</Box>
                  </Box>
              ),
              sort: { get: (row) => row.subject, initialDir: 'asc' },
          }
        : {
              key: 'subject',
              header: 'Person',
              label: (row) => row.subject,
              fill: (row) => Math.round(row.rate * 100),
              share: (row) => formatPercent(row.rate),
              sort: { get: (row) => row.rate, initialDir: 'desc' },
          };

    const columns: RankedListColumn<AnomalyRow>[] = [];
    if (!isDrift) {
        columns.push({
            key: 'severity',
            header: 'Severity',
            width: 84,
            raw: true,
            render: (row) => (
                <Box style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <SeverityPill severity={row.severity} />
                </Box>
            ),
            sort: { get: (row) => SEVERITY_RANK[row.severity], initialDir: 'desc' },
        });
    }
    columns.push({ key: 'count', header: isDrift ? 'Topics' : 'Re-prompts', width: METRIC_WIDTH, render: (row) => formatCount(row.count), sort: { get: (row) => row.count, initialDir: 'desc' } });
    if (isDrift) {
        columns.push({ key: 'turns', header: 'Prompts', width: METRIC_WIDTH, render: (row) => formatCount(row.turns), sort: { get: (row) => row.turns, initialDir: 'desc' }, visibleFrom: 'sm' });
    }
    columns.push({ key: 'totalTokens', header: isDrift ? 'Total tokens' : 'Re-prompt tokens', width: isDrift ? TOKEN_WIDTH : 116, render: (row) => formatTokens(row.totalTokens), sort: { get: (row) => row.totalTokens, initialDir: 'desc' }, visibleFrom: 'sm', size: 'xs' });

    return (
        <RankedList
            rows={rows}
            primary={primaryCol}
            columns={columns}
            rowKey={(row) => row.drillKey ?? row.subject}
            accent={definition.accent}
            initialSort={{ key: isDrift ? 'totalTokens' : 'subject', dir: 'desc' }}
            hrefFor={(row) => hrefFor(row.drillKey)}
            unitPlural={definition.lensPlural}
        />
    );
}
